import React, { useEffect, useCallback, useRef } from "react";
import { EmblaCarouselType } from "embla-carousel";
import useEmblaCarousel from "embla-carousel-react";
import { hapticFeedback } from "@telegram-apps/sdk-react";
import { cn } from "@/lib/utils";

const CIRCLE_DEGREES = 360;
const WHEEL_ITEM_SIZE = 32;
const WHEEL_ITEM_COUNT = 18;
const WHEEL_ITEMS_IN_VIEW = 4;

export const WHEEL_ITEM_RADIUS = CIRCLE_DEGREES / WHEEL_ITEM_COUNT;
export const IN_VIEW_DEGREES = WHEEL_ITEM_RADIUS * WHEEL_ITEMS_IN_VIEW;
export const WHEEL_RADIUS = Math.round(
  WHEEL_ITEM_SIZE / 2 / Math.tan(Math.PI / WHEEL_ITEM_COUNT)
);

const isInView = (wheelLocation: number, slidePosition: number): boolean =>
  Math.abs(wheelLocation - slidePosition) < IN_VIEW_DEGREES;

const setSlideStyles = (
  emblaApi: EmblaCarouselType,
  index: number,
  loop: boolean,
  slideCount: number,
  totalRadius: number
): void => {
  const slideNode = emblaApi.slideNodes()[index];
  const wheelLocation = emblaApi.scrollProgress() * totalRadius;
  const positionDefault = emblaApi.scrollSnapList()[index] * totalRadius;
  const positionLoopStart = positionDefault + totalRadius;
  const positionLoopEnd = positionDefault - totalRadius;

  let inView = false;
  let angle = index * -WHEEL_ITEM_RADIUS;

  if (isInView(wheelLocation, positionDefault)) {
    inView = true;
  }

  if (loop && isInView(wheelLocation, positionLoopEnd)) {
    inView = true;
    angle = -CIRCLE_DEGREES + (slideCount - index) * WHEEL_ITEM_RADIUS;
  }

  if (loop && isInView(wheelLocation, positionLoopStart)) {
    inView = true;
    angle = -(totalRadius % CIRCLE_DEGREES) - index * WHEEL_ITEM_RADIUS;
  }

  if (inView) {
    slideNode.style.opacity = "1";
    slideNode.style.transform = `translateY(-${
      index * 100
    }%) rotateX(${angle}deg) translateZ(${WHEEL_RADIUS}px)`;
  } else {
    slideNode.style.opacity = "0";
    slideNode.style.transform = "none";
  }
};

export const setContainerStyles = (
  emblaApi: EmblaCarouselType,
  wheelRotation: number
): void => {
  emblaApi.containerNode().style.transform = `translateZ(${WHEEL_RADIUS}px) rotateX(${wheelRotation}deg)`;
};

type PropType = {
  loop?: boolean;
  perspective: "left" | "right" | "center";
  onValueChange?: (value: number) => void;
  className?: string;
  value?: number;
  items: {
    label: string;
    value: number;
  }[];
};

export const IosPickerItem: React.FC<PropType> = (props) => {
  const {
    perspective,
    loop = false,
    onValueChange,
    value,
    items,
    className,
  } = props;
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop,
    axis: "y",
    dragFree: true,
    containScroll: false,
    watchSlides: false,
  });
  const hasInitializedRef = useRef(false);
  const slideCount = items.length;
  const rootNodeRef = useRef<HTMLDivElement>(null);
  const totalRadius = slideCount * WHEEL_ITEM_RADIUS;
  const rotationOffset = loop ? 0 : WHEEL_ITEM_RADIUS;
  const lastIndexRef = useRef<number | null>(null);

  const inactivateEmblaTransform = useCallback(
    (emblaApi: EmblaCarouselType) => {
      if (!emblaApi) return;
      const { translate, slideLooper } = emblaApi.internalEngine();
      translate.clear();
      translate.toggleActive(false);
      slideLooper.loopPoints.forEach(({ translate }) => {
        translate.clear();
        translate.toggleActive(false);
      });
    },
    []
  );

  const rotateWheel = useCallback(
    (emblaApi: EmblaCarouselType) => {
      const rotation = slideCount * WHEEL_ITEM_RADIUS - rotationOffset;
      const wheelRotation = rotation * emblaApi.scrollProgress();
      setContainerStyles(emblaApi, wheelRotation);
      emblaApi.slideNodes().forEach((_, index) => {
        setSlideStyles(emblaApi, index, loop, slideCount, totalRadius);
      });
    },
    [slideCount, rotationOffset, totalRadius, loop]
  );

  // Track scroll changes and trigger haptic feedback when passing items
  const handleScroll = useCallback(
    (emblaApi: EmblaCarouselType) => {
      rotateWheel(emblaApi);

      // Calculate current index based on scroll progress
      const progress = emblaApi.scrollProgress();
      const snapCount = emblaApi.scrollSnapList().length;
      const currentIndex = Math.round(progress * (snapCount - 1));

      // If this is different from the last index, trigger haptic feedback
      if (
        lastIndexRef.current !== null &&
        lastIndexRef.current !== currentIndex
      ) {
        hapticFeedback.impactOccurred("soft");
      }

      lastIndexRef.current = currentIndex;
    },
    [rotateWheel]
  );

  useEffect(() => {
    if (!emblaApi) return;

    emblaApi.on("pointerUp", (emblaApi) => {
      const { scrollTo, target, location } = emblaApi.internalEngine();
      const diffToTarget = target.get() - location.get();
      const factor = Math.abs(diffToTarget) < WHEEL_ITEM_SIZE / 2.5 ? 10 : 0.1;
      const distance = diffToTarget * factor;
      scrollTo.distance(distance, true);
    });

    emblaApi.on("scroll", handleScroll);

    emblaApi.on("reInit", (emblaApi) => {
      inactivateEmblaTransform(emblaApi);
      rotateWheel(emblaApi);
    });

    inactivateEmblaTransform(emblaApi);
    rotateWheel(emblaApi);

    return () => {
      emblaApi.off("scroll", handleScroll);
    };
  }, [emblaApi, inactivateEmblaTransform, rotateWheel, handleScroll]);

  const handleSelect = useCallback(
    (api: EmblaCarouselType) => {
      const selectedIndex = api.selectedScrollSnap();
      const selectedValue = items[selectedIndex]?.value;
      onValueChange?.(selectedValue);
    },
    [items, onValueChange]
  );

  useEffect(() => {
    if (!emblaApi) return;

    emblaApi.on("select", handleSelect);

    return () => {
      emblaApi?.off("select", handleSelect);
    };
  }, [emblaApi, items, onValueChange, handleSelect]);

  useEffect(() => {
    if (!emblaApi || value === undefined) return;

    const index = items.findIndex((item) => item.value === value);
    if (index === -1) return;

    const scrollToIndex = () => {
      emblaApi.scrollTo(index, !hasInitializedRef.current);
      hasInitializedRef.current = true;
    };

    scrollToIndex();
  }, [emblaApi, value, items]);

  return (
    <div className={cn("embla__ios-picker", className)}>
      <div className="embla__ios-picker__scene" ref={rootNodeRef}>
        <div
          className={`embla__ios-picker__viewport embla__ios-picker__viewport--perspective-${perspective}`}
          ref={emblaRef}
        >
          <div className="embla__ios-picker__container relative">
            {items.map(({ label }, index) => (
              <div className="embla__ios-picker__slide" key={index}>
                {label}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
